import { useState } from 'react'
import { Box, IconButton, Tooltip, Typography, Grid, Divider } from '@mui/material'

import { Button } from '@/@core/components/mui/Button'
import { TableLimit } from '@/@core/components/mui/TableLimit'
import { useRouteQuery, useTranslator } from '@/hooks'
import { FilterButton } from '@/components/common'
import CompanySelection from '@/components/selection/CompanySelection'
import InputDateRangerField from '@/components/common/input/InputDateRangerField'
import { SelectColumns } from '@/components/common/SelectColumns'
import AppLogs from '@/components/common/AppLogs'
import { ActivityLogTypeEnum } from '@/@data'

interface ColumnOption {
  key: string
  label: string
}

interface BalanceFilterTableProps {
  onStatistic?: () => void
  openStatistic?: boolean
  onSelectColumns?: (columns: string[]) => void
  columns?: ColumnOption[]
  visibleColumns?: string[]
}

export const BalanceFilterTable = ({
  onStatistic,
  openStatistic,
  visibleColumns,
  columns,
  onSelectColumns
}: BalanceFilterTableProps) => {
  const { __ } = useTranslator()
  const { updateQuery, searchQuery } = useRouteQuery()
  const [filter, setFilter] = useState(true)
  const [isOpenLogs, setIsOpenLogs] = useState(false)

  return (
    <Box
      sx={{
        display: 'flex',
        padding: '20px',
        gap: 4,
        flexDirection: 'column'
      }}
    >
      {filter && (
        <>
          <Box className='mb-3'>
            <Typography variant='h6' className='mb-3'>
              {__('Filters')}
            </Typography>
            <Grid container spacing={2}>
              <Grid size={6}>
                <CompanySelection
                  fullWidth
                  value={searchQuery.get('company_id_table') || ''}
                  onChange={company_id_table => {
                    updateQuery({ company_id_table, page: 1 })
                  }}
                  onClear={() => {
                    updateQuery({ company_id_table: '', page: 1 })
                  }}
                />
              </Grid>
              <Grid size={6}>
                <InputDateRangerField
                  value={
                    searchQuery.get('contract_from')
                      ? [searchQuery.get('contract_from') as string, searchQuery.get('contract_to') as string]
                      : []
                  }
                  placeholder={[__('Start date contract'), __('End date contract')]}
                  onChange={contract_at => {
                    if (contract_at) {
                      updateQuery({ contract_from: contract_at[0], contract_to: contract_at[1], page: 1 })
                    } else {
                      updateQuery({ contract_from: undefined, contract_to: undefined, page: 1 })
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Box>
          <Divider />
        </>
      )}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <TableLimit />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant='tonal'
            color={openStatistic ? 'primary' : 'secondary'}
            size='small'
            startIcon={<i className='tabler-chart-bar' />}
            onClick={onStatistic}
          >
            {__('Statistics On')}
          </Button>
          <FilterButton filter={filter} onFilter={() => setFilter(!filter)} />
          <div>
            <SelectColumns
              options={
                columns?.map(column => {
                  const isChecked = visibleColumns?.includes(column.key) ?? true

                  return {
                    id: column.key,
                    label: column.label,
                    checked: isChecked
                  }
                }) || []
              }
              onChange={options => {
                const selectedColumns = options.filter(option => option.checked).map(option => option.id)

                onSelectColumns?.(selectedColumns)
              }}
              buttonText={__('Columns')}
              showTotalSelected={false}
            />
          </div>

          <Tooltip title='History'>
            <IconButton
              size='small'
              onClick={() => setIsOpenLogs(true)}
              sx={{
                width: 40,
                height: 40,
                backgroundColor: '#f5f5f5',
                '&:hover': {
                  backgroundColor: '#e0e0e0'
                }
              }}
            >
              <i className='tabler-history' style={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
        <AppLogs
          open={isOpenLogs}
          setOpen={setIsOpenLogs}
          title={__('History Menu Balance')}
          object_type={ActivityLogTypeEnum.Finance}
        />
      </Box>
    </Box>
  )
}
